/**
 * The auth hero headline, split into the part that is set in ink and the part that carries the
 * accent.
 *
 * KANON writes this as `<h1>…<span class="accent">word.</span></h1>` and the approved auth mockup
 * reproduces it on the photo pane: `Twoja wiedza, <em>ich wyniki.</em>`. The accent there is one of
 * only two ember uses on the whole sign-in screen (the other is the focus ring), which is what
 * "the accent is a scalpel" means in practice.
 *
 * The split is a rule rather than a hardcoded substring because the copy is translated: the accent
 * has to land on the right words in Polish AND English without either being spelled out here.
 * The rule is the clause after the final comma, falling back to the final word — which reproduces
 * the mockup exactly for `auth.land.r2` in both languages:
 *
 *   "Twoja wiedza, ich wyniki."      → "Twoja wiedza, " + "ich wyniki."
 *   "Your knowledge, their results." → "Your knowledge, " + "their results."
 *
 * Pure. Takes a string, returns two strings.
 */

export interface AccentedHeadline {
  /** Set in `--text-on-photo`. Empty when the whole line is accented. */
  readonly lead: string;
  /** Set in `--accent-on-photo`. Never empty for a non-empty input. */
  readonly accent: string;
}

/**
 * Split a headline into its lead and its accented tail.
 *
 * A line with neither a comma nor a space is entirely accented — there is nothing to lead with,
 * and returning an empty accent would silently drop the design's only ember mark.
 */
export function splitAccentedHeadline(headline: string): AccentedHeadline {
  const trimmed = headline.trim();
  if (trimmed.length === 0) return { lead: '', accent: '' };

  const lastComma = trimmed.lastIndexOf(',');
  if (lastComma !== -1) {
    const accent = trimmed.slice(lastComma + 1).trimStart();
    // The lead keeps the comma and the space after it, so lead + accent is the original line.
    if (accent.length > 0) {
      return { lead: trimmed.slice(0, trimmed.length - accent.length), accent };
    }
  }

  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace === -1) return { lead: '', accent: trimmed };

  return { lead: trimmed.slice(0, lastSpace + 1), accent: trimmed.slice(lastSpace + 1) };
}
